import React from "react";
import { Check, ChevronRight } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { iconFor } from "@/components/Connectors/connectorIcons";
import type { Connector } from "@/hooks/useConnectors";

// Whole-row targets rather than a row with a button in it. Pressing a connector should open the
// connector — the setup action belongs on the detail pane, where there is room to say what it does
// before the user commits to it.

export interface ConnectorSection {
  key: string;
  label: string;
  connectors: Connector[];
}

interface ConnectorListProps {
  connectors: Connector[];
  /** Category sections to render under headings. Null renders one flat list. */
  sections?: ConnectorSection[] | null;
  search: string;
  onSelect: (connector: Connector) => void;
  onClearSearch: () => void;
  onRequestConnector: () => void;
}

const ConnectorList: React.FC<ConnectorListProps> = ({
  connectors,
  sections,
  search,
  onSelect,
  onClearSearch,
  onRequestConnector,
}) => {
  if (connectors.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center dark:border-white/10">
        <p className="text-sm font-medium text-foreground">
          {search ? `Nothing matches "${search.trim()}"` : "Nothing here yet"}
        </p>
        <p className="mx-auto mt-1 max-w-xs text-xs leading-relaxed text-muted-foreground">
          Try a different name, or tell us what you're missing and we'll look at adding it.
        </p>
        <div className="mt-3 flex flex-wrap justify-center gap-2">
          {search && (
            <button
              type="button"
              onClick={onClearSearch}
              className="rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-gray-50 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/10"
            >
              Clear search
            </button>
          )}
          <button
            type="button"
            onClick={onRequestConnector}
            className="rounded-md bg-ink-blue-900 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-ink-blue-800 dark:bg-white/10 dark:hover:bg-white/20"
          >
            Request a connector
          </button>
        </div>
      </div>
    );
  }

  const rows = (items: Connector[]) => (
    <ul className="space-y-1.5">
      {items.map((connector) => {
        const Icon = iconFor(connector.icon);
        const unavailable = connector.available === false;

        return (
          <li key={connector.key}>
            <button
              type="button"
              onClick={() => onSelect(connector)}
              className="flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 text-left transition-colors hover:border-gray-300 hover:bg-gray-50/60 dark:border-white/10 dark:bg-white/5 dark:hover:border-white/20 dark:hover:bg-white/10"
            >
              <div
                className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                  unavailable
                    ? "bg-gray-100 text-muted-foreground dark:bg-white/5 dark:text-gray-600"
                    : connector.connected
                      ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400"
                      : "bg-gray-100 text-muted-foreground dark:bg-white/10 dark:text-gray-400"
                }`}
              >
                <Icon className="h-7 w-7" />
                {connector.connected && !unavailable && (
                  <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-emerald-500 dark:border-card" />
                )}
              </div>

              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-foreground">
                  {connector.name}
                  {connector.connected && !unavailable && (
                    <Badge
                      variant="secondary"
                      className="gap-1 bg-emerald-50 px-1.5 py-0 text-[10px] font-medium text-emerald-700 hover:bg-success-bg dark:text-emerald-400 dark:hover:bg-emerald-950/30"
                    >
                      <Check className="h-2.5 w-2.5" />
                      Connected
                    </Badge>
                  )}
                  {unavailable && (
                    <Badge
                      variant="secondary"
                      className="bg-gray-100 px-1.5 py-0 text-[10px] font-medium text-muted-foreground hover:bg-gray-100 dark:bg-white/10 dark:text-gray-400 dark:hover:bg-white/10"
                    >
                      Off for workspace
                    </Badge>
                  )}
                  {!connector.connected && !unavailable && connector.beta && (
                    <Badge
                      variant="secondary"
                      className="bg-soft px-1.5 py-0 text-[10px] font-medium text-muted-foreground hover:bg-soft dark:bg-white/10 dark:text-gray-300 dark:hover:bg-white/10"
                    >
                      Beta
                    </Badge>
                  )}
                </p>
                <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                  {connector.summary}
                </p>
              </div>

              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          </li>
        );
      })}
    </ul>
  );

  // Headings only where they carry information. Inside one category, or across a search result, a
  // heading on every row repeats what the user just chose.
  if (!sections) return rows(connectors);

  return (
    <div className="space-y-5">
      {sections.map((section) => (
        <section key={section.key} className="space-y-2">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {section.label}
          </h3>
          {rows(section.connectors)}
        </section>
      ))}
    </div>
  );
};

export default ConnectorList;
